import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { FormSection, SelectField, TextAreaField, TextField } from "@/components/common/Forms";
import { FileUpload } from "@/components/common/FileUpload";
import { PageTitle } from "@/components/shell/DashboardShell";
import { REQUEST_TYPES } from "@/data/portal";

const PRIORITIES = [{ value: "normal", label: "Normal" }, { value: "high", label: "High" }, { value: "urgent", label: "Urgent (safety risk)" }];

const DynamicField = ({ field, value, onChange }) =>
  field.type === "select" ? (
    <SelectField id={`req-${field.name}`} label={field.label} value={value} onChange={onChange} options={field.options} />
  ) : (
    <TextField id={`req-${field.name}`} label={field.label} type={field.type} value={value} onChange={onChange} />
  );

export default function NewRequest() {
  const { type } = useParams();
  const navigate = useNavigate();
  const cfg = REQUEST_TYPES[type];
  const [f, setF] = useState({ subject: "", site_location: "", priority: "normal", description: "" });
  const [details, setDetails] = useState({});
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  if (!cfg) return <Navigate to="/portal" replace />;
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/requests", { ...f, type, details, attachment_ids: files.map((x) => x.id) });
      toast.success(`Request ${data.reference} submitted`);
      navigate(`/portal/requests/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl" data-testid={`new-request-page-${type}`}>
      <Link to="/portal" className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="new-request-back"><ArrowLeft className="h-4 w-4" /> Dashboard</Link>
      <PageTitle eyebrow="New request" title={cfg.label} text={cfg.blurb} />
      <div className="mb-6 flex flex-wrap gap-2">
        {Object.entries(REQUEST_TYPES).map(([k, t]) => (
          <Link key={k} to={`/portal/new/${k}`} className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${k === type ? "border-[#123634] bg-[#123634] text-white" : "border-slate-300 bg-white text-slate-600 hover:border-slate-500"}`} data-testid={`new-request-type-${k}`}>{t.label}</Link>
        ))}
      </div>
      <form onSubmit={submit} className="card-base space-y-8 p-6 sm:p-10" data-testid="new-request-form">
        <FormSection step="01" title="Request summary">
          <TextField id="req-subject" label="Subject" value={f.subject} onChange={set("subject")} required className="col-span-full" placeholder="e.g. Quarterly AMC renewal for Pune campus" />
          <TextField id="req-site" label="Site / location" value={f.site_location} onChange={set("site_location")} required />
          <SelectField id="req-priority" label="Priority" value={f.priority} onChange={set("priority")} options={PRIORITIES} />
        </FormSection>
        <FormSection step="02" title={`${cfg.label} details`}>
          {cfg.fields.map((fd) => (
            <DynamicField key={fd.name} field={fd} value={details[fd.name]} onChange={(v) => setDetails((p) => ({ ...p, [fd.name]: v }))} />
          ))}
          <TextAreaField id="req-description" label="Describe the requirement" value={f.description} onChange={set("description")} required rows={5} className="col-span-full" />
        </FormSection>
        <FormSection step="03" title="Attachments">
          <div className="col-span-full"><FileUpload id="req-files" files={files} onChange={setFiles} label="Photos, invoices, service reports (optional)" /></div>
        </FormSection>
        <button type="submit" disabled={busy} className="btn-primary" data-testid="new-request-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit request</button>
      </form>
    </div>
  );
}
